import type { LegalBlock } from './legalContentTypes';

export const PRIVACY_POLICY_CONTENT: LegalBlock[] = [
  {
    type: 'paragraph',
    text: 'Worker Solutions, LLC is a limited liability company established in the United States. This Privacy Policy describes how Worker Solutions® and its affiliates (“Worker Solutions®”, “we” “us” or “our”) collect and use information when you interact with our website www.workersolutions.com, (collectively the “Website”), when you apply for a job with us, or when you otherwise engage with us.',
    links: [{ match: 'www.workersolutions.com', href: 'https://www.workersolutions.com/', bold: true }],
  },
  {
    type: 'paragraph',
    text: 'This Privacy Policy does not apply to websites, apps, or online services that have their own privacy statements.',
  },
  {
    type: 'paragraph',
    text: 'This Privacy Policy also provides you with information about the rights you may have.',
  },
  {
    type: 'paragraph',
    text: 'Please read this Privacy Policy carefully and consult our Terms of Use for more information about the general terms and conditions regarding your use of the Website.',
  },
  {
    type: 'paragraph',
    text: 'Worker Solutions® reserves the right, at our discretion, to change our Privacy Policy at any time without prior notice. When we do, we will post the revised Privacy Policy on this page with a new "Last Updated" date. The revised Privacy Policy will become effective at the time of posting. We suggest you review this Privacy Policy periodically for changes. If you wish to contact Worker Solutions® regarding your Personal Information (as defined below) or this policy, please contact info@workersolutions.com.',
    links: [{ match: 'info@workersolutions.com', href: 'mailto:info@workersolutions.com', bold: true }],
  },
  {
    type: 'paragraph',
    text: 'By using and accessing this website, apps or online services provided by Worker Solutions, you acknowledge that you have read and agree to this Privacy Policy.',
  },
  { type: 'heading', text: '1. THE TYPES OF PERSONAL INFORMATION WE COLLECT' },
  {
    type: 'paragraph',
    text: 'For purposes of this Policy, “Personal Information” means information that identifies, relates to, describes, is reasonably capable of being associated with, or could reasonably be linked, directly or indirectly, with a particular person or household, such as name, email address or phone number, account number or account information.',
  },
  {
    type: 'paragraph',
    text: 'In order to access or request certain services provided via the Website, to apply for a job with us, to fill out calculators, forms or other fields or when you otherwise interact with us via phone, email or mail, we will collect Personal Information. For example, you may provide your name, company name, address, institution, city, state, zip/postal code, country, email address, phone number, fax number, username, password, employment and educational history (for job applicants) and any other information that you choose to provide to us in your subject line and message.',
  },
  {
    type: 'paragraph',
    text: 'If you choose to not provide us with this or other Personal Information, you might not be able to access certain information and services or successfully apply for a job with us.',
  },
  {
    type: 'paragraph',
    text: 'We may also collect information about you via automated means. For example, when you visit the Website, we may collect your IP address, your browser type, your operating system, the pages you view on the Website, the pages you view immediately before and after you access the Website, and the search terms you enter on the Website. We collect this information using cookies and other technical means. Cookies are small files that a webpage sends to your computer or device while you are viewing the webpage. A more detailed description of cookies is described below.',
  },
  {
    type: 'paragraph',
    text: 'The Website may also use other technical methods to track visitor usage, including web beacons. “Web beacons” are small pieces of data that are embedded in images on the pages of a webpage. We use these technical methods to analyze the traffic patterns on the Website, such as the frequency with which our users visit various parts of the Website. These technical methods may involve the transmission of information either directly to us or to another party authorized by us to collect information on our behalf. We may also use these technical methods in HTML e-mails that we send users of the Website to determine whether such visitors have opened those e-mails and/or clicked on links in those e-mails.',
  },
  { type: 'heading', text: '2. STATE-SPECIFIC INFORMATION' },
  {
    type: 'paragraph',
    text: 'Certain states, including California, to the extent any of these state laws apply, require us to provide additional disclosures with respect to our collection, use, and disclosure of Personal Information. This information in this section is provided as a supplement to the rest of the information provided in this Policy. Please read the entire Policy for a full description of our privacy practices.',
  },
  {
    type: 'paragraph',
    text: 'We do not sell or share Personal Information for cross context behavioral advertising. We also do not engage in profiling or automated decision making.',
  },
  {
    type: 'paragraph',
    text: 'The Personal Information we collect includes information within the below categories of data. Note that the “category of data” column listed below refers to the category of personal data as defined under California law and represents the categories of Personal Information that we have collected, and how it has been shared, over the past 12 months, if applicable. Inclusion of a category in the list below indicates only that we may collect some information within that category. We do not necessarily collect all information listed in a particular category, nor do we necessarily collect all categories of information for all individuals. While the categories are defined under California law, the disclosures below also apply to residents of other states.',
  },
  {
    type: 'paragraph',
    text: 'We may disclose Personal Information in each of the below categories with our affiliates and service providers for our business purposes, as well as with government entities as necessary. We have not and do not necessarily share all information listed in a category. We may also share your Internet or other electronic network activity information (as described further below) with our advertising partners but only based on your consent.',
  },
  { type: 'subheading', text: 'CATEGORIES OF DATA' },
  {
    type: 'paragraph',
    boldLead: 'Personal Identifiers',
    text: 'Identifiers such as a real name, alias, postal address, unique personal identifier, online identifier Internet Protocol address, email address, account name, social security number, driver’s license number, passport number, or other similar identifiers.',
  },
  { type: 'paragraph', text: 'We collect information in this category:' },
  {
    type: 'list',
    items: [
      'directly from you or your interactions with our website;',
      'from records we have about you in the course of providing services or products; and/or',
      'from recruitment agencies.',
    ],
  },
  {
    type: 'paragraph',
    text: 'A subset of this data is processed in connection with our operational functions, including:',
  },
  {
    type: 'list',
    items: [
      'to provide, maintain and improve our product services;',
      'to monitor and analyze trends, usage and activity related to our products; and',
      'to detect, investigate and prevent fraudulent transactions and other illegal activities as well as protect the rights and property of us and others.',
    ],
  },
  {
    type: 'paragraph',
    text: 'We also use this information for marketing purposes and, for job applicants, to open, maintain and evaluate your candidacy.',
  },
  {
    type: 'paragraph',
    boldLead: 'Information About You',
    text: 'Information that identifies, relates to, describes, or is capable of being associated with, a particular individual, including, but not limited to, your name, signature, Social Security number, physical characteristics or description, address, telephone number, passport number, driver’s license or state identification card number, insurance policy number, education, employment, employment history, bank account number, credit card number, debit card number, or any other financial information, medical information, or health insurance information.',
  },
  { type: 'paragraph', text: 'We collect information in this category:' },
  {
    type: 'list',
    items: [
      'directly from you or your interactions with our website;',
      'from records we have about you in the course of providing services or products;',
      'from third-party marketing providers; and/or',
      'from recruitment agencies',
    ],
  },
  {
    type: 'paragraph',
    text: 'A subset of this data is processed in connection with our operational functions, including:',
  },
  {
    type: 'list',
    items: [
      'to provide, maintain and improve our product services;',
      'to monitor and analyze trends, usage and activity related to our products; and',
      'to detect, investigate and prevent fraudulent transactions and other illegal activities as well as protect the rights and property of us and others.',
    ],
  },
  {
    type: 'paragraph',
    text: 'We also use this information for marketing purposes and, for job applicants, to open, maintain and evaluate your candidacy.',
  },
  {
    type: 'paragraph',
    boldLead: 'Internet or other electronic network activity information',
    text: 'Browsing history, search history, and information regarding a consumer’s interaction with an Internet Web site, application, or advertisement.',
  },
  {
    type: 'paragraph',
    text: 'We collect information in this category directly from you or your interactions with our website.',
  },
  {
    type: 'paragraph',
    text: 'This data is processed in connection with our operational functions, including:',
  },
  {
    type: 'list',
    items: [
      'to provide, maintain and improve our product services;',
      'to monitor and analyze trends, usage and activity related to our products;',
      'to detect, investigate and prevent fraudulent transactions and other illegal activities as well as protect the rights and property of Us and others;',
      'to defend our legal rights, as needed; and',
      'for sales-related purposes.',
    ],
  },
  {
    type: 'paragraph',
    text: 'We also use this information to advertise products/services that might be of interest to you.',
  },
  {
    type: 'paragraph',
    boldLead: 'Geolocation data',
    text: 'Data that relates to a consumer’s physical location.',
  },
  {
    type: 'paragraph',
    text: 'We collect information in this category directly from you or your interactions with our website.',
  },
  {
    type: 'paragraph',
    text: 'A subset of this data is processed in connection with our operational functions, including:',
  },
  {
    type: 'list',
    items: [
      'to provide, maintain and improve our product services;',
      'to monitor and analyze trends, usage and activity related to our products; and',
      'to detect, investigate and prevent fraudulent transactions and other illegal activities as well as protect the rights and property of us and others.',
    ],
  },
  { type: 'paragraph', text: 'We also use this information for marketing purposes.' },
  {
    type: 'paragraph',
    boldLead: 'Sensory information',
    text: 'Audio, electronic, visual, thermal, olfactory or similar information, including voice signatures and recorded calls.',
  },
  {
    type: 'paragraph',
    text: 'We collect information in this category directly from you or your interactions with our website.',
  },
  {
    type: 'paragraph',
    text: 'A subset of this data (especially calls to us, which may be recorded) is processed in connection with our operational functions, including:',
  },
  {
    type: 'list',
    items: [
      'to provide, maintain and improve our services; and',
      'to detect, investigate and prevent fraudulent transactions and other illegal activities as well as protect the rights and property of us and others.',
    ],
  },
  { type: 'paragraph', text: 'We also use this information for marketing purposes.' },
  { type: 'subheading', text: 'Artificial Intelligence and Automated Tools' },
  {
    type: 'paragraph',
    text: 'We may use artificial intelligence, machine learning, large language models, and other automated decision-making or generative technologies (“AI tools”) to process your Personal Information in connection with recommending, providing and improving our services and website.',
  },
  {
    type: 'paragraph',
    text: 'We may also use your Personal Information and interaction data to train, retrain, evaluate, and improve our AI models and services, as well as our related technologies. This may include using aggregated, anonymized, de-identified, or otherwise processed data to enhance model performance, accuracy, safety, and user experience. AI Tools may generate preliminary insights based on your information; however, such outputs do not constitute professional advice.',
  },
  {
    type: 'paragraph',
    text: 'Where applicable, we implement administrative, technical, and physical safeguards designed to protect personal information processed through AI tools and measures designed to minimize the use of identifiable Personal Information in model training.',
  },
  {
    type: 'paragraph',
    text: 'AI-generated content, recommendations, responses, and feedback are provided solely for general informational and educational purposes and may contain errors or inaccurate or outdated information. These AI tools and the content they generate are not a substitute for advice from a qualified financial advisor, investment professional, accountant, attorney, or other licensed professional. If you do not agree with the processing of your personal data by our AI tools, you should not use any of our AI-enabled features.',
  },
  {
    type: 'paragraph',
    text: 'We do not use AI tools as automated decision-making technology and our AI-generated content does not approve, deny, or otherwise make decisions regarding any consumer’s access to services.',
  },
  { type: 'subheading', text: 'ADDITIONAL DATA' },
  {
    type: 'paragraph',
    text: 'In addition to the categories of Personal Information above, we may collect the following categories of sensitive Personal Information. We only process sensitive Personal Information for necessary processes.',
  },
  {
    type: 'paragraph',
    boldLead: 'Social security number, driver’s license, state identification card, or passport number',
    text: 'This information is collected directly from you or our service providers.',
  },
  {
    type: 'paragraph',
    text: 'This information is necessary to administer your relationship with us, including meeting our legal obligations, and providing you with benefits and services, as applicable. For job applicants, it is necessary to verify your right to work.',
  },
  {
    type: 'paragraph',
    boldLead:
      'Information concerning a consumer’s health, sex life, or sexual orientation; or information about racial or ethnic origin, religious or philosophical beliefs, or union membership',
    text: 'This information is collected directly from you, either directly or through publicly available sites like LinkedIn, or through recruitment agencies.',
  },
  {
    type: 'paragraph',
    text: 'For job applicants, this information is necessary to provide you with any necessary accommodations during the employment candidacy process and to further our DEI goals and objective.',
  },
  { type: 'subheading', text: 'Retention of Personal Information' },
  {
    type: 'paragraph',
    text: 'We consider the following criteria when determining how long a particular record will be retained, including any Personal Information contained in that record:',
  },
  {
    type: 'list',
    items: [
      'How long the record is needed to provide you with the products and services you request',
      'How long the record is needed to support and enhance our operational processes',
      'How long the record is needed to protect our rights and legal interests',
      'How long the record must be retained to comply with applicable laws and regulations',
    ],
  },
  {
    type: 'paragraph',
    text: 'The same Personal Information about you may be included in more than one record and used for more than one purpose, each of which may be subject to different retention periods based on the factors listed above.',
  },
  { type: 'subheading', text: 'Your Rights and Choices Regarding Personal Information' },
  {
    type: 'paragraph',
    text: 'Depending on your state of residency, and the type of data collected, you may have rights with respect to Personal Information about you. We may, at our discretion, choose to extend these rights to you even if we are not required to under applicable law.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to access/know',
    leadSeparator: ' \u2013 ',
    text: 'You may be entitled to request that we disclose to you the Personal Information we have collected about you, including the categories of Personal Information, the categories of sources from which we collected the information, the business or commercial purposes of collecting the information, the categories of third parties with whom we have shared the information, and the categories of Personal Information that we have shared with third parties for a business purpose. In some instances, you may have the right to receive the information about you we have collected in a portable and readily usable format.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to data portability',
    leadSeparator: ' \u2013 ',
    text: 'Subject to certain conditions, you may be entitled to request that we disclose to you the Personal Information we have collected about you in a portable, and to the extent feasible, usable format that allows you to transmit the data to another entity without hindrance.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to correct',
    leadSeparator: ' \u2013 ',
    text: 'Subject to certain conditions, you may have request that we correct any mistakes in Personal Information about you or update your preferences; however, we may not be able to accommodate your request if we believe it would violate any law or legal requirement or cause the information to be incorrect. We may delete the contested Personal Information as an alternative to correcting the information if the deletion of the Personal Information does not negatively impact you, or you consent to the deletion.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to deletion',
    leadSeparator: ' \u2013 ',
    text: 'Subject to certain conditions, you may be entitled to request that we delete Personal Information about you. Before deleting information, we must be able to verify your identity. We will not delete Personal Information about you when the information is required to fulfill a legal obligation, is necessary to exercise or defend legal claims, or where we are required or permitted to retain the information by law. For example, we cannot delete information about you if your Personal Information is a necessary element of the contract between us.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to non-discrimination',
    leadSeparator: ' \u2013 ',
    text: 'You may have the right to not be discriminated against for exercising any of the above-listed rights. We may, however, provide a different level of service or charge a different rate reasonably relating to the value of your Personal Information.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to opt-out of the sale or sharing of Personal Information',
    leadSeparator: ' \u2013 ',
    text: 'We currently do not sell or share Personal Information for purposes of cross contextual behavioral/targeted advertising.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to opt-in to processing of sensitive personal information',
    leadSeparator: ' \u2013 ',
    text: 'We currently only collect sensitive personal information when you provide it to us.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to limit use and disclosure of sensitive Personal Information',
    leadSeparator: ' \u2013 ',
    text: 'We only process sensitive Personal Information for purposes considered necessary under applicable law, but for job applicants, if you would like to limit our use of accessibility information, please let us know.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to opt out of receiving our marketing emails',
    leadSeparator: ' \u2013 ',
    text: 'To stop receiving our promotional emails, follow the instructions in any promotional message you get from us. If you object to our use of your information for direct marketing purposes, please email us at info@workersolutions.com. Even if you opt out of getting marketing messages, we will still send you transactional messages. These include responses to your questions.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to limit how we use cookies and tracking tools',
    leadSeparator: ' \u2013 ',
    text: 'You are prompted to select your choices for non-essential cookies and tracking tools when you first visit our site, and we will periodically confirm your choices, which you can modify. More information on cookies and related technologies is below, including on how we also have tools in place to better detect and honor requests made using the Global Privacy Control (“GPC”) signal.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to know and question a profiling decision',
    leadSeparator: ' \u2013 ',
    text: 'We currently do not engage in profiling or automated decision making.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to be informed as to why the profiling resulted in the decision and how the consumer could have secured a different decision',
    leadSeparator: ' \u2013 ',
    text: 'We currently do not engage in profiling or automated decision making.',
  },
  {
    type: 'paragraph',
    boldLead: 'Right to review and correct data and reevaluate decisions related to profiling',
    leadSeparator: ' \u2013 ',
    text: 'We currently do not engage in profiling or automated decision making.',
  },
  { type: 'paragraph', text: 'We do not discriminate against you if you choose to exercise any of these rights.' },
  {
    type: 'paragraph',
    text: 'If we deny your request, you may also have the right to know the reason your request was denied and the right to appeal that decision. Instructions on how to appeal any denial will be included in the notice of denial.',
  },
  {
    type: 'paragraph',
    text: 'Depending on your residency, these rights may not apply to pseudonymous data if the information necessary to identify the consumer is kept separately and is subject to controls that prevent access to the information. Pseudonymous data is personal data that can no longer be attributed to a specific individual without the use of additional information, if the additional information is kept separately and is subject measures to ensure that personal data is not attributed to the specific individual.',
  },
  {
    type: 'paragraph',
    text: 'Data solely retained for data backup or archive purposes is principally excluded from these rights until it is restored to an active system or next accessed or used for a sale, disclosure, or commercial purpose.',
  },
  {
    type: 'paragraph',
    text: 'Please note that under applicable privacy law, we are only obligated to respond to Personal Information requests from the same consumer up to two times in a 12-month period. In addition, under applicable privacy law, and for the protection of your Personal Information, we may be limited in what Personal Information we can disclose.',
  },
  {
    type: 'paragraph',
    text: 'Depending on your residency, we will maintain Consumer Data Rights requests for at least 2 years. This information will not be used for any other purpose except to review compliance processes; it will not be shared except as necessary to comply with a legal obligation. Depending on your residency, sensitive data which we no longer have consent to process will be deleted or rendered permanently anonymized or inaccessible within a reasonable period of time after withdrawal of consent.',
  },
  {
    type: 'paragraph',
    text: 'You may also be entitled to request in writing a list of the types of Personal Information we have disclosed to a third party for their direct marketing purposes during the preceding year and to whom that information was disclosed.',
  },
  { type: 'subheading', text: 'Submitting Privacy Requests' },
  {
    type: 'paragraph',
    text: 'You can exercise your privacy rights by submitting requests to us and by taking other steps to limit how information about you is collected, used, and shared.',
  },
  {
    type: 'paragraph',
    text: 'If your state grants the consumer rights referenced above, to the extent applicable, you may exercise your privacy rights by submitting a Personal Information request by submitting a Personal Data Request Form by visiting this link, completing the form and emailing the completed form to info@workersolutions.com.',
    links: [
      { match: 'this link', href: 'https://ty3ah.share.hsforms.com/2Gfy0d0QsStKrKIUFKa1z3w', bold: true },
      { match: 'info@workersolutions.com', href: 'mailto:info@workersolutions.com', bold: true },
    ],
  },
  {
    type: 'paragraph',
    text: 'We must verify your identity before fulfilling your Personal Information request. To verify your identity, we match information from you with information we have on file, including, to the extent applicable, your name, date of birth, contact information, or your account information. Information collected for purposes of verifying your request will only be used for verification and to respond to your Personal Information request. If you maintain an account with us, we may require you to log in to that account as part of submitting your request.',
  },
  {
    type: 'paragraph',
    text: 'If we are unable to verify your identity as part of your request, we will not be able to satisfy your request. We are not obligated to collect additional information in order to enable you to verify your identity. For deletion requests, you will be required to submit a verifiable request for deletion and then confirm separately that you want Personal Information about you deleted.',
  },
  {
    type: 'paragraph',
    text: 'If you would like to appoint an authorized agent to make a request on your behalf, we require you to verify your identity with us directly before we provide any requested information to your approved agent. Alternatively, your authorized agent may provide evidence of having power of attorney or acting as a conservator for you. Note that we may require you to verify your identity with us directly before we provide any requested information to your authorized agent unless your authorized agent has power of attorney or acts as a conservator, in which case we will not contact you directly. We may also request that any authorized agents verify their identity and may reach out to you directly to confirm that you have provided the agent with your permission to submit the request on your behalf. There may be circumstances where we will not be able to honor your request. We will notify you if we are unable to honor your request. Based on your residency, you may have the right to appeal our decision with regards to your request by using the “Contact Us” details provided at the end of this Policy.',
  },
  { type: 'heading', text: '3. OTHER WEBSITES' },
  {
    type: 'paragraph',
    text: 'The Website may contain links to websites operated by third parties, including the payment processor Stripe. Should you choose to visit any of these third-party sites, you should review those sites’ privacy policies to ensure you understand and are comfortable with their practices concerning your information. We disclaim any responsibility for the privacy policies and information practices of any third-party website (whether such site is linked on or to the Website). These links are provided to you for convenience purposes only, and you access them at your own risk.',
  },
  {
    type: 'paragraph',
    text: 'We may link the information that we collect automatically with the information you provide us. And we may combine the information we collect online with information we collect offline or that is collected by third parties.',
  },
  { type: 'heading', text: '4. NEWSLETTERS' },
  {
    type: 'paragraph',
    text: 'When you sign up to receive email notifications from us on our Website, we will send the communications that you have requested (including information about corporate events, press releases, presentations and company documents). You can opt-out of receiving such communications at any time by contacting us or by clicking “unsubscribe” in the email.',
  },
  { type: 'heading', text: '5. COOKIES AND RELATED TECHNOLOGIES' },
  { type: 'subheading', text: 'Gathering and Use of Technical Usage and Personal Information' },
  {
    type: 'paragraph',
    text: 'Like most website operators, we gather technical usage information that web browsers, depending on their settings, make available. As we want to be responsive to you and to ensure the proper functioning of our products and organization, we will use the information collected to:',
  },
  {
    type: 'list',
    items: [
      'ensure content from the Website is presented in the most effective manner for you and your device;',
      'allow you to navigate or browse through the Website quickly and efficiently;',
      'monitor and analyze trends, usage and activity in connection with our Website and services to improve the Website;',
      'administer the Website and for internal operations, in order to conduct troubleshooting, data analysis, testing, research, statistical and survey analysis;',
      'protect our rights, your rights and the rights of others;',
      'investigate and respond to any complaints,',
      'comply with legal obligations;',
      'measure and understand the effectiveness of the content we serve to you and others, including developing reports regarding pages visited. In this case, Personal Information is used in aggregate form, and does not reveal the Personal Information of a particular user;',
      'verify the user’s eligibility to access any restricted areas of the Website, including the Website; and',
      'provide users with customer support.',
    ],
  },
  {
    type: 'paragraph',
    text: 'We may use information that does not reasonably identify you for any purpose. For example, for research purposes we may prepare aggregated reports about how visitors to the Website tend to interact with the Website.',
  },
  { type: 'subheading', text: 'What are cookies?' },
  {
    type: 'paragraph',
    text: 'A cookie is a piece of information that a website stores on a user’s computer, and that the user’s browser provides to the website each time the user submits a query to the website using that browser.',
  },
  {
    type: 'paragraph',
    text: 'When you access the Website, we place cookies on your computer. A cookie allows us to determine that a visit has occurred within our site, as well as which pages were viewed. Through cookies, we may collect your Internet Protocol (“IP”) address, operating system and browser type, and the locations of the web pages you view right before arriving at, while navigating and immediately after leaving the Website.',
  },
  {
    type: 'paragraph',
    text: 'You can find more information about cookies and how to manage them at http://www.allaboutcookies.org/.',
    links: [{ match: 'http://www.allaboutcookies.org/', href: 'http://www.allaboutcookies.org/', bold: true }],
  },
  { type: 'subheading', text: 'Do Not Track?' },
  {
    type: 'paragraph',
    text: 'Various third parties are developing ways for consumers to express their choice about the collection of the individual consumer’s online activities over time and across third-party websites or online services. While we collect cookies with your consent, and while we will periodically ask you to confirm or modify your choices. We also have tools in place to better detect and honor requests made using the Global Privacy Control (“GPC”) signal as requests to opt out of the sharing of Personal Information to the extent required by applicable law. We endeavor to honor these requests with respect to any Personal Information connected to the GPC opt out request based on the information made available by GPC. Note that we may not be able to tie a GPC request to all Personal Information we have about you.',
  },
  { type: 'heading', text: '6. WORKER SOLUTIONS®’ SHARING OF INFORMATION WITH THIRD PARTY PROVIDERS' },
  { type: 'subheading', text: 'Service Providers' },
  {
    type: 'paragraph',
    text: 'Sometimes we engage companies that act on our behalf in connection with the operation of the Website. For example, such companies may deliver products or services, improve the functionality of the Website, collect information about you, communicate with you, or store information for us. In some cases, these companies may need to access information about you. Worker Solutions® endeavors to require our service providers by contract to collect, store, and use information about you only on our behalf, and in accordance with the provisions of this Privacy Policy.',
  },
  { type: 'subheading', text: 'Analytics Providers' },
  {
    type: 'paragraph',
    text: 'We may engage third-party analytics providers to help us understand how users engage with the Website. These third-parties may use cookies and similar technologies to collect information about your use of the Website.',
  },
  { type: 'subheading', text: 'Disclosure of Personal Information to Selected Third Parties' },
  { type: 'paragraph', text: 'We may share your information with selected recipients. These categories of recipients include:' },
  {
    type: 'list',
    items: [
      'Recruitment providers to assist us in candidate recruitment; and',
      'Where you have asked to be notified of corporate events we may use email management service companies.',
    ],
  },
  { type: 'paragraph', text: 'And/or:' },
  {
    type: 'list',
    items: [
      'Service providers to perform certain services on our behalf such as:',
      'to process and store data, including your Personal Information;',
      'to track, analyze, and modify our Website;',
      'for marketing, advertising, and distribution;',
      'for co-sponsoring or co-branding certain services, promotions or events;',
      'to assist us in providing you with customer support; and',
      'to support our IT and security efforts.',
    ],
  },
  { type: 'subheading', text: 'Other Sharing Practices' },
  {
    type: 'paragraph',
    text: 'We may share your information with our affiliates and subsidiaries, and may also provide information to our attorneys, banks, auditors, and other service providers.',
  },
  { type: 'paragraph', text: 'We may disclose information as:' },
  {
    type: 'list',
    items: [
      'required by law, including, for example, to comply with a court order or subpoena, or in response to a law enforcement agency’s request;',
      'to help to protect our rights or enforce our Terms of Use;',
      'to support our detection, prevention, or response to fraud or intellectual property infringement; and',
      'to help protect your safety or security, including the safety and security of property that belongs to you.',
    ],
  },
  {
    type: 'paragraph',
    text: 'We may also share information about you to the extent that it is reasonably necessary to proceed with the consideration, negotiation, or completion of a merger, reorganization, or acquisition of our business, or a sale, liquidation, or transfer of some or all of our assets.',
  },
  { type: 'heading', text: '7. SECURITY MEASURES WE TAKE TO SAFEGUARD INFORMATION' },
  {
    type: 'paragraph',
    text: 'The security and confidentiality of the information we collect or receive is extremely important to us, and we take our responsibility to protect this information very seriously. We have implemented commercially reasonable technical, administrative, and physical security measures to protect the information that we collect or receive.',
  },
  {
    type: 'paragraph',
    text: 'From time to time, we review and consider updates for our security procedures. Please be aware that, despite our ongoing efforts, no security measures are perfect or impenetrable. This means that we cannot ensure or warrant the security of any information we collect about you, and therefore your use of the Website is at your own risk.',
  },
  { type: 'heading', text: '8. CHILDREN’S INFORMATION.' },
  {
    type: 'paragraph',
    text: 'The Website is intended for use by those 18 years or older. Persons under the age of 18 are not permitted to use Worker Solutions’ Website or services and we do not knowingly solicit or collect Personal Information from children under the age of 18. If we learn that, despite these measures, a child under the age of 18 has submitted Personal Information through the Website, we will take reasonable measures to delete all such information and shall only use such information where necessary to protect the safety of the child or others as required by law.',
  },
  {
    type: 'paragraph',
    text: 'We receive limited personal information about children from parents/guardians and other responsible adults only as required to receive information, establish and maintain accounts for the child’s benefit.',
  },
  { type: 'heading', text: '9. HOW CAN YOU CONTACT US?' },
  {
    type: 'paragraph',
    text: 'If you would like to contact us about this policy, our Website, please reach out to us via the Contact page. You can also write to our privacy lead at info@workersolutions.com.',
    links: [{ match: 'info@workersolutions.com', href: 'mailto:info@workersolutions.com', bold: true }],
  },
  { type: 'heading', text: '10. SUPPLEMENTAL PRIVACY NOTICES' },
  { type: 'subheading', text: 'International Users' },
  {
    type: 'paragraph',
    text: 'Worker Solutions provides services intended solely for visitors residing within the United States. The information we provide and the third-party products and services featured are tailored for the United States market only. You agree that if you choose to use the Worker Solutions Website from outside the United States, you will be transferring your information outside of your respective region to the United States for storage and processing.',
  },
];
